import React, { useEffect, useState } from 'react';
import { useGetMany, useNotify } from 'react-admin';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';

import { IRecomputeReport, recomputePartnerCommissions } from '../../Services/partners';

// Recalcul des commissions d'un partenaire, en DEUX temps : simulation à l'ouverture,
// création sur confirmation. On crée de l'argent dû à un tiers — la liste se lit avant.
//
// Sert après une correction des termes (début antidaté, association tardive) : le moteur
// ne commissionne qu'au fil des paiements, il ne revient jamais sur un encaissement passé.
// Seuls les ENCAISSEMENTS comptent : paiements Stripe et factures Pennylane payées.

type Props = {
	partnerId: string;
	open: boolean;
	onClose: () => void;
	onApplied: () => void;
};

const eur = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtDate = (d: string) => new Date(d).toLocaleDateString('fr-FR');

// #region useRecompute — simulation à l'ouverture, puis application
const useRecompute = (partnerId: string, open: boolean, onApplied: () => void) => {
	const notify = useNotify();
	const [report, setReport] = useState<IRecomputeReport | null>(null);
	const [loading, setLoading] = useState(false);
	const [applying, setApplying] = useState(false);

	useEffect(() => {
		if (!open) return;
		setReport(null);
		setLoading(true);
		recomputePartnerCommissions(partnerId, false)
			.then(setReport)
			.catch((e: any) => notify(`Erreur : ${e?.message || 'simulation impossible'}`, { type: 'error' }))
			.finally(() => setLoading(false));
	}, [partnerId, open, notify]);

	const apply = () => {
		setApplying(true);
		recomputePartnerCommissions(partnerId, true)
			.then((r) => {
				notify(`${r.lines.length} commission(s) créée(s), ${eur(r.total)}`, { type: 'success' });
				onApplied();
			})
			.catch((e: any) => notify(`Erreur : ${e?.message || 'recalcul impossible'}`, { type: 'error' }))
			.finally(() => setApplying(false));
	};

	return { report, loading, applying, apply };
};
// #endregion

// #region RecomputeSummary — ce qui n'est PAS créé, et pourquoi
const RecomputeSummary: React.FC<{ report: IRecomputeReport }> = ({ report }) => (
	<>
		<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
			Encaissements relus : paiements Stripe et factures Pennylane payées. {report.alreadyCommissioned} déjà
			commissionné(s) · {report.outOfWindow} hors fenêtre de commissionnement.
		</Typography>
		{/* Jamais supprimées ici : une ligne a pu être débloquée, voire versée. La reprendre
		    est un geste de compta (ajustement wallet), pas l'effet d'un recalcul. */}
		{report.existingOutOfWindow > 0 && (
			<Alert severity="warning" sx={{ mt: 2 }}>
				{report.existingOutOfWindow} commission(s) existante(s) tombent désormais hors de la fenêtre. Elles ne
				sont pas retirées : à corriger à la main par un ajustement si besoin.
			</Alert>
		)}
	</>
);
// #endregion

// #region RecomputeCommissionsDialog
export const RecomputeCommissionsDialog: React.FC<Props> = ({ partnerId, open, onClose, onApplied }) => {
	const { report, loading, applying, apply } = useRecompute(partnerId, open, () => {
		onApplied();
		onClose();
	});

	const baIds = Array.from(new Set((report?.lines || []).map((l) => l.billingAccountId)));
	const { data: accounts } = useGetMany<any>('billing-accounts', { ids: baIds }, { enabled: baIds.length > 0 });
	const nameById = new Map<string, string>((accounts || []).map((a: any) => [String(a.id), a.name as string]));

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>Recalculer les commissions</DialogTitle>
			<DialogContent>
				{loading || !report ? (
					<Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
						<CircularProgress size={24} />
					</Box>
				) : (
					<>
						{report.lines.length === 0 ? (
							<Typography variant="body2">Aucune commission manquante.</Typography>
						) : (
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Client</TableCell>
										<TableCell>Facture</TableCell>
										<TableCell>Encaissé le</TableCell>
										<TableCell align="right">Montant HT</TableCell>
										<TableCell align="right">Taux</TableCell>
										<TableCell align="right">Commission</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{report.lines.map((l) => (
										<TableRow key={`${l.attributionId}-${l.invoiceId}`}>
											<TableCell>{nameById.get(l.billingAccountId) || l.billingAccountId}</TableCell>
											<TableCell>{l.invoiceId}</TableCell>
											<TableCell>{fmtDate(l.collectedAt)}</TableCell>
											<TableCell align="right">{eur(l.collectedAmount)}</TableCell>
											<TableCell align="right">{l.rate} %</TableCell>
											<TableCell align="right" sx={{ fontWeight: 700 }}>
												{eur(l.amount)}
											</TableCell>
										</TableRow>
									))}
									<TableRow>
										<TableCell colSpan={5} sx={{ fontWeight: 700 }}>
											Total
										</TableCell>
										<TableCell align="right" sx={{ fontWeight: 700 }}>
											{eur(report.total)}
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						)}
						<RecomputeSummary report={report} />
					</>
				)}

				<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
					<Button onClick={onClose}>Annuler</Button>
					<Button variant="contained" onClick={apply} disabled={applying || !report || report.lines.length === 0}>
						Créer {report?.lines.length || 0} commission(s)
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
};
// #endregion
